// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
const INTL_CACHE = {};
/**
 * Turns out the Intl functions are very slow...
 *
 * Cache is indexed first by locale and then by format, should give us pretty
 * good cache hit rate.
 *
 * Returns a cached instance of Intl.DateTimeFormat
 */ function getFormatter(locale, format) {
    const localeKey = Array.isArray(locale) ? locale.join('-') : locale;
    if (!INTL_CACHE[localeKey]) {
        INTL_CACHE[localeKey] = new WeakMap();
    }
    if (INTL_CACHE[localeKey].has(format)) {
        return INTL_CACHE[localeKey].get(format);
    }
    INTL_CACHE[localeKey].set(format, new Intl.DateTimeFormat(locale, format));
    return INTL_CACHE[localeKey].get(format);
}
/**
 * We have limited set of formats so things are kept consistent
 */ export const DATE_FORMATS = {
    DateTime: {
        Full: {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        },
        FullTimeZone: {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'short'
        },
        Short: {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }
    },
    Date: {
        Full: {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        },
        Short: {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        },
        MonthYear: {
            month: 'short',
            year: 'numeric'
        },
        DayMonth: {
            month: 'short',
            day: 'numeric'
        },
        DayLongMonth: {
            month: 'long',
            day: 'numeric'
        }
    }
};
export const TIME_FORMATS = {
    HourMinute: {
        hour: 'numeric',
        minute: '2-digit'
    }
};
/**
 * Take either a ISO8601 string or unix timestamp and returns Date
 *
 * @param toParse ISO8601 | unix timestamp
 * @returns Date
 */ export function parseIsoDateOrTimestamp(toParse) {
    if (toParse === undefined || toParse === '') {
        return new Date(Date.now());
    }
    if (typeof toParse === 'string') {
        if (toParse.match(/^[0-9]+$/)) {
            return new Date(parseInt(toParse) * 1000);
        }
        let parsed = Date.parse(toParse);
        if (isNaN(parsed)) {
            // Some browsers cant handle ISO8601 we split reformat it to:
            // 2015/01/11 00:06:44-08:00
            const parts = toParse.split('T');
            parsed = Date.parse(`${parts[0].replace(/-/g, '/')} ${parts[1]}`);
            if (isNaN(parsed)) {
                return new Date(Date.now());
            }
        }
        return new Date(parsed);
    }
    return new Date(toParse * 1000);
}
/**
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
 *
 * date should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 */ export function formatDate(date, format, locale = 'en-US') {
    if (!date) {
        return '';
    }
    try {
        const parsedDate = parseIsoDateOrTimestamp(date);
        return getFormatter(locale, format).format(parsedDate);
    } catch (e) {
        if (e.name === 'RangeError') {
            try {
                const parsedDate1 = parseIsoDateOrTimestamp(date);
                return parsedDate1.toISOString();
            } catch (err) {
                return '';
            }
        }
        return '';
    }
}
/**
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleTimeString
 *
 * datetime should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 */ export function formatTime(datetime, format, locale = 'en-US') {
    if (!datetime) {
        return '';
    }
    try {
        const parsedDate = parseIsoDateOrTimestamp(datetime);
        return getFormatter(locale, format).format(parsedDate);
    } catch (e) {
        if (e.name === 'RangeError') {
            try {
                const parsedDate1 = parseIsoDateOrTimestamp(datetime);
                return parsedDate1.toISOString();
            } catch (err) {
                return '';
            }
        }
        return '';
    }
}
/**
 * date should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 *
 * @param date ISO8601 string
 */ export function formatServerDate(date) {
    try {
        const parsedDate = parseIsoDateOrTimestamp(date);
        return `${parsedDate.getFullYear()}-${parsedDate.getMonth()}-${parsedDate.getDate()}`;
    } catch (e) {
        return '';
    }
}
/**
 * dates should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 *
 * @param timespan1 ISO8601 string
 * @param timespan2 ISO8601 string
 * @param unit
 */ export function dateDiff(timespan1, timespan2, unit) {
    if (!timespan1 || !timespan2) {
        return 0;
    }
    try {
        const date1 = parseIsoDateOrTimestamp(timespan1);
        const date2 = parseIsoDateOrTimestamp(timespan2);
        const diffSeconds = Math.floor((date1.getTime() - date2.getTime()) / 1000);
        switch(unit){
            case 'minutes':
                return Math.floor(diffSeconds / 60);
            case 'hours':
                return Math.floor(diffSeconds / 3600);
            case 'days':
                return Math.floor(diffSeconds / 86400);
            case 'weeks':
                return Math.floor(diffSeconds / 604800);
            case 'months':
                return Math.floor(diffSeconds / 2592000);
            case 'years':
                return Math.floor(diffSeconds / 31536000);
            default:
                return diffSeconds;
        }
    } catch (e) {
        return 0;
    }
}
function invertedDateDiff(timespan1, timespan2, unit) {
    return dateDiff(timespan2, timespan1, unit);
}
/**
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 *
 * dates should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * Long:
 *  0-59 secs: "Just now"
 *  1-59 mins: "5 mins ago"
 *  1-23 hours: "5 hours ago"
 *  24-48 hours: "Yesterday"
 *  1-6 days: "5 days ago"
 *  6+ days: "Nov 15, 2018"
 * Short:
 *  0-59 secs: "Now"
 *  1-59 mins: "5m"
 *  1-23 hours: "5h"
 *  24-47 hours: "1d"
 *  1-6 days: "5d"
 *  6+ days: "Nov 15, 2018"
 *
 * @param dateTo ISO8601 string
 * @param short
 */ export function dateToRelativeTrans(dateTo, short = false) {
    const makeKey = (key)=>short ? `${key}.short` : key;
    const between = (n, left, right)=>n >= left && n <= right;
    const now = new Date(Date.now());
    const midnight = new Date(new Date(Date.now()).setHours(0, 0, 0, 0));
    const diffSeconds = dateDiff(now.toISOString(), dateTo, 'seconds');
    // 0-59 secs: "Just now"
    if (diffSeconds < 0 || between(diffSeconds, 0, 59)) {
        return {
            key: makeKey('date.relative.just'),
            value: 0
        };
    }
    // 1-59 mins: "5 mins ago"
    const diffMins = dateDiff(now.toISOString(), dateTo, 'minutes');
    if (between(diffMins, 1, 59)) {
        return {
            key: makeKey('date.relative.minutes'),
            value: diffMins
        };
    }
    // 1 day ago: "Yesterday"
    // (dateDiff returns the number of full days in the interval)
    // Check yesterday first, in case it's less than 24 hours
    const diffHours = dateDiff(now.toISOString(), dateTo, 'hours');
    const diffDays = dateDiff(midnight.toISOString(), dateTo, 'days') + 1;
    if (diffDays === 1) {
        return {
            key: makeKey('date.relative.yesterday'),
            value: short ? diffDays : diffHours
        };
    }
    // 1-23 hours: "5 hours ago"
    if (between(diffHours, 1, 23)) {
        return {
            key: makeKey('date.relative.hours'),
            value: diffHours
        };
    }
    // 2-6 days: "5 days ago"
    if (between(diffDays, 2, 6)) {
        return {
            key: makeKey('date.relative.days'),
            value: diffDays
        };
    }
    // 6+ days: "Nov 15, 2018"
    if (diffDays > 6) {
        return {
            key: makeKey('date.relative.date'),
            value: formatDate(dateTo, DATE_FORMATS.Date.Short)
        };
    }
    return {
        key: makeKey('date.relative.just'),
        value: 0
    };
}
/**
 * On server we render UTC on client we render users TZ (adjusted from UTC)
 *
 * dates should be ISO8601 with TZ 2015-01-11T00:06:44-08:00 or at worst timestamp,
 * if API returns something different get them to fix it.
 *
 * Long:
 * 0-59 secs: "a few seconds"
 * 1-3 mins: "a few minutes"
 * 3-59 mins: "8 minutes"
 * 1-3 hours: "a few hours"
 * 4-23 hours: "5 hours"
 * 24-47 hours: "about a day"
 * 1-6 days: "5 days"
 * 1-4 weeks: "3 weeks"
 * 2-11 months: "4 months"
 * 12 months: "about a year",
 * 13+ months: "1 year",
 * Short:
 * 0-59 secs: "1s"
 * 1-59 mins: "1m"
 * 1-3 hours: "1h"
 * 4-23 hours: "5h"
 * 24-47 hours: "1d"
 * 1-6 days: "5d"
 * 1-4 weeks: "3w"
 * 2-11 months: "4m"
 * 12+ months: "1y"
 *
 * @param dateTo ISO8601 string
 * @param short
 */ export function dateToDistanceTrans(dateTo, short = false, invert = false) {
    const makeKey = (key)=>short ? `${key}.short` : key;
    const between = (n, left, right)=>n >= left && n <= right;
    const dateDiffFunction = invert ? invertedDateDiff : dateDiff;
    const now = new Date(Date.now());
    // 0-59 secs: "a few seconds"
    const diffSecs = dateDiffFunction(now.toISOString(), dateTo, 'seconds');
    if (between(diffSecs, 0, 59)) {
        return {
            key: between(diffSecs, 0, 3) && !short ? makeKey('date.distance.few.seconds') : makeKey('date.distance.seconds'),
            value: diffSecs
        };
    }
    const diffMins = dateDiffFunction(now.toISOString(), dateTo, 'minutes');
    if (between(diffMins, 1, 59)) {
        return {
            key: between(diffMins, 1, 3) && !short ? makeKey('date.distance.few.minutes') : makeKey('date.distance.minutes'),
            value: diffMins
        };
    }
    // 1-23 hours: "5 hours ago"
    const diffHours = dateDiffFunction(now.toISOString(), dateTo, 'hours');
    if (between(diffHours, 1, 23)) {
        return {
            key: between(diffHours, 1, 3) && !short ? makeKey('date.distance.few.hours') : makeKey('date.distance.hours'),
            value: diffHours
        };
    }
    // 24-28 hours: about a day
    if (between(diffHours, 24, 28)) {
        return {
            key: makeKey('date.distance.about_a_day'),
            value: 1
        };
    }
    // 1-6 days: "5 days"
    const diffDays = dateDiffFunction(now.toISOString(), dateTo, 'days');
    if (between(diffDays, 1, 6)) {
        return {
            key: makeKey('date.distance.days'),
            value: diffDays
        };
    }
    const diffWeeks = dateDiffFunction(now.toISOString(), dateTo, 'weeks');
    if (between(diffWeeks, 1, 4)) {
        return {
            key: makeKey('date.distance.weeks'),
            value: diffWeeks
        };
    }
    const diffMonths = dateDiffFunction(now.toISOString(), dateTo, 'months');
    if (between(diffMonths, 1, 11)) {
        return {
            key: makeKey('date.distance.months'),
            value: diffMonths
        };
    } else if (diffMonths === 12) {
        return {
            key: short ? makeKey('date.distance.years') : makeKey('date.distance.about_a_year'),
            value: 1
        };
    }
    const diffYears = dateDiffFunction(now.toISOString(), dateTo, 'years');
    if (diffYears > 0) {
        return {
            key: makeKey('date.distance.years'),
            value: diffYears
        };
    }
    return {
        key: makeKey('date.distance.few.seconds'),
        value: 1
    };
}
export default {
    DATE_FORMATS,
    formatDate,
    dateToRelativeTrans,
    dateDiff
};
